import React, { useState } from "react";
import { adminApiRequest } from "../../utils/adminApi";

const emptyBundle = { qty: "", price_single: "", price_double: "" };
const emptyGroup = { group_label: "", bundles: [{ ...emptyBundle }] };

export default function PrintItemModal({
  isOpen,
  onClose,
  onSubmit,
  form,
  setForm,
  saving,
  isEdit,
  categories = [],
  masterExtras = [],
  onReloadExtras,
  error,
  success,
}) {
  const [localLoading, setLocalLoading] = useState(false);
  if (!isOpen) return null;

  const handleFormChange = (key, value) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const handleGroupLabelChange = (gIdx, value) => {
    const newGroups = [...form.groups];
    newGroups[gIdx].group_label = value;
    handleFormChange("groups", newGroups);
  };

  const addGroup = () => {
    setForm((prev) => ({
      ...prev,
      groups: [
        ...prev.groups,
        { ...emptyGroup, bundles: [{ ...emptyBundle }] },
      ],
    }));
  };

  const duplicateGroup = (gIdx) => {
    const groupToCopy = form.groups[gIdx];
    const newGroup = {
      ...groupToCopy,
      group_label: groupToCopy.group_label
        ? `${groupToCopy.group_label} (Copy)`
        : "",
      bundles: groupToCopy.bundles.map((b) => ({
        ...b,
        id: undefined, // Clear ID so it's created as new rows
      })),
    };
    setForm((prev) => ({
      ...prev,
      groups: [...prev.groups, newGroup],
    }));
  };

  const removeGroup = (gIdx) => {
    if (form.groups.length <= 1) return;
    handleFormChange(
      "groups",
      form.groups.filter((_, i) => i !== gIdx),
    );
  };

  const addBundle = (gIdx) => {
    const newGroups = [...form.groups];
    newGroups[gIdx].bundles.push({ ...emptyBundle });
    handleFormChange("groups", newGroups);
  };

  const handleBundleChange = (gIdx, bIdx, key, value) => {
    const newGroups = [...form.groups];
    newGroups[gIdx].bundles[bIdx][key] = value;
    handleFormChange("groups", newGroups);
  };

  const removeBundle = (gIdx, bIdx) => {
    if (form.groups[gIdx].bundles.length <= 1) return;
    const newGroups = [...form.groups];
    newGroups[gIdx].bundles = newGroups[gIdx].bundles.filter(
      (_, i) => i !== bIdx,
    );
    handleFormChange("groups", newGroups);
  };

  const handleExtraChange = (index, key, value) => {
    const newExtras = [...form.extras];
    newExtras[index][key] = value;
    handleFormChange("extras", newExtras);
  };

  // Format a price value to always show 2 decimal places on blur
  const formatPrice = (raw) => {
    const n = parseFloat(raw);
    return isNaN(n) ? "0.00" : n.toFixed(2);
  };

  const handleBundleBlur = (gIdx, bIdx, key) => {
    const newGroups = [...form.groups];
    newGroups[gIdx].bundles[bIdx][key] = formatPrice(newGroups[gIdx].bundles[bIdx][key]);
    handleFormChange("groups", newGroups);
  };

  const handleExtraBlur = (index) => {
    const newExtras = [...form.extras];
    newExtras[index].price = formatPrice(newExtras[index].price);
    handleFormChange("extras", newExtras);
  };

  const addExtraRow = () => {
    handleFormChange("extras", [...form.extras, { name: "", price: "0.00" }]);
  };

  const removeExtraRow = (index) => {
    handleFormChange(
      "extras",
      form.extras.filter((_, i) => i !== index),
    );
  };

  const onLocalSubmit = async (e) => {
    if (e) e.preventDefault();
    try {
      // 1. Process extras first
      setLocalLoading(true);
      const updatedExtras = [];
      for (const ex of form.extras) {
        if (!ex.name.trim()) continue;

        if (ex.id) {
          const master = masterExtras.find(m => String(m.id) === String(ex.id));
          const nameChanged = master && master.name !== ex.name;
          const priceChanged = master && Number(master.price) !== Number(ex.price);

          if (isEdit && (nameChanged || priceChanged)) {
            if (priceChanged && !nameChanged) {
              // Price changed on a linked extra — create a NEW separate entry
              // so the original master record is not affected globally
              const res = await adminApiRequest({
                script: "create_print_extra",
                data: { name: ex.name, price: ex.price }
              });
              if (res?.success) {
                updatedExtras.push({ ...ex, id: res.id });
              } else {
                updatedExtras.push(ex);
              }
              continue;
            } else {
              // Name changed (or both changed) — update the master record
              await adminApiRequest({
                script: "update_print_extra",
                data: { id: ex.id, name: ex.name, price: ex.price }
              });
            }
          }
          updatedExtras.push(ex);
        } else {
          // New extra (no id) — create it as a master extra so it's reusable
          const res = await adminApiRequest({
            script: "create_print_extra",
            data: { name: ex.name, price: ex.price }
          });
          if (res?.success) {
            updatedExtras.push({ ...ex, id: res.id });
          } else {
            updatedExtras.push(ex); // Fallback to local if creation fails
          }
        }
      }

      // Update form extras with the new IDs before final submission
      handleFormChange("extras", updatedExtras);
      
      // Reload master list for other modals
      if (onReloadExtras) await onReloadExtras();

      setLocalLoading(false);
      // Final submission
      onSubmit(e);
    } catch (err) {
      setLocalLoading(false);
      console.error("Error processing extras:", err);
    }
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-slate-900/60 p-4 backdrop-blur-sm">
      <div className="max-h-[92vh] w-full max-w-4xl overflow-hidden rounded-2xl bg-white shadow-2xl flex flex-col">
        <div className="flex items-start justify-between gap-3 border-b border-slate-200 px-5 py-4 bg-slate-50/50">
          <div>
            <h3 className="text-lg font-semibold text-slate-900">
              {isEdit ? "Edit Print Item" : "Add Print Item"}
            </h3>
            <p className="text-sm text-slate-500">
              Set the category, variations, and pricing bundles below.
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            disabled={saving}
            className="text-slate-400 hover:text-slate-600 transition p-2 hover:bg-slate-100 rounded-full disabled:opacity-30"
          >
            <i className="fas fa-times"></i>
          </button>
        </div>

        <form onSubmit={onLocalSubmit} className="flex-1 overflow-hidden flex flex-col">
          <div className="flex-1 overflow-y-auto px-6 py-6">
            
            {/* IN-MODAL FEEDBACK */}
            {(error || success) && (
              <div className="mb-6 animate-in slide-in-from-top-2 duration-300">
                {error && (
                  <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-600 font-medium flex items-center gap-3">
                    <i className="fas fa-exclamation-circle"></i>
                    {error}
                  </div>
                )}
                {success && (
                  <div className="rounded-xl border border-green-200 bg-green-50 px-4 py-3 text-sm text-green-600 font-medium flex items-center gap-3">
                    <i className="fas fa-check-circle"></i>
                    {success}
                  </div>
                )}
              </div>
            )}
            {/* ITEM DETAILS */}
            <div className="grid gap-6 md:grid-cols-2 mb-10">
              <div className="space-y-1.5">
                <label className="text-sm text-slate-600">Category</label>
                <select
                  value={form.category_id}
                  onChange={(e) =>
                    handleFormChange("category_id", e.target.value)
                  }
                  className="w-full rounded-xl border border-slate-200 bg-slate-50/50 px-4 py-2.5 text-sm outline-none focus:border-slate-900 focus:bg-white transition"
                  required
                >
                  <option value="">Select a category</option>
                  {categories.map((cat) => (
                    <option key={cat.id} value={cat.id}>
                      {cat.name}
                    </option>
                  ))}
                </select>
              </div>
              <div className="space-y-1.5">
                <label className="text-sm text-slate-600">
                  Size / Option
                </label>
                <input
                  type="text"
                  value={form.size}
                  onChange={(e) => handleFormChange("size", e.target.value)}
                  className="w-full rounded-xl border border-slate-200 bg-slate-50/50 px-4 py-2.5 text-sm outline-none focus:border-slate-900 focus:bg-white transition"
                  placeholder="e.g. A5"
                  required
                />
              </div>
            </div>

            {/* WEIGHTS & QUANTITIES */}
            <div className="space-y-8 mb-10">
              {form.groups.map((group, gIdx) => (
                <div
                  key={gIdx}
                  className="rounded-2xl border border-slate-200 bg-white overflow-hidden"
                >
                  <div className="bg-slate-50 px-5 py-3 border-b border-slate-200 flex items-center justify-between">
                    <div className="flex items-center gap-4 flex-1 max-w-sm">
                      <label className="text-xs font-medium text-slate-500 uppercase tracking-wider">
                        Group Label:
                      </label>
                      <input
                        type="text"
                        value={group.group_label}
                        onChange={(e) => handleGroupLabelChange(gIdx, e.target.value)}
                        className="flex-1 bg-transparent border-b border-slate-300 text-slate-900 outline-none focus:border-slate-900 py-0.5 text-sm font-medium"
                        placeholder="e.g. 100gsm, Gloss, Standard"
                      />
                    </div>
                    <div className="flex items-center gap-3">
                      <button
                        type="button"
                        onClick={() => duplicateGroup(gIdx)}
                        className="text-blue-600 hover:text-blue-700 text-xs font-medium transition"
                      >
                        Duplicate Group
                      </button>
                      {form.groups.length > 1 && (
                        <button
                          type="button"
                          onClick={() => removeGroup(gIdx)}
                          className="text-red-600 hover:text-red-700 text-xs font-medium transition"
                        >
                          Remove Group
                        </button>
                      )}
                    </div>
                  </div>

                  <div className="p-5">
                    <div className="overflow-hidden rounded-xl border border-slate-100 mb-4">
                      <table className="w-full text-left text-sm">
                        <thead className="bg-slate-50/50 border-b border-slate-100">
                          <tr className="text-xs text-slate-400 font-medium">
                            <th className="px-4 py-3">Quantity</th>
                            <th className="px-4 py-3">Single-Sided Price</th>
                            <th className="px-4 py-3">
                              Double-Sided Price (Optional)
                            </th>
                            <th className="px-4 py-3 text-right"></th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-50">
                          {group.bundles.map((bundle, bIdx) => (
                            <tr key={bIdx} className="align-top">
                              <td className="px-2 py-3">
                                <input
                                  type="number"
                                  value={bundle.qty}
                                  onChange={(e) =>
                                    handleBundleChange(
                                      gIdx,
                                      bIdx,
                                      "qty",
                                      e.target.value,
                                    )
                                  }
                                  className="w-full bg-slate-50/50 px-3 py-2 rounded-lg text-sm outline-none border border-transparent focus:border-slate-200 focus:bg-white transition"
                                  placeholder="e.g. 500"
                                  required
                                />
                              </td>
                              <td className="px-2 py-3">
                                <div className="relative">
                                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-xs">
                                    £
                                  </span>
                                  <input
                                    type="number"
                                    step="0.01"
                                    value={bundle.price_single}
                                    onChange={(e) =>
                                      handleBundleChange(
                                        gIdx,
                                        bIdx,
                                        "price_single",
                                        e.target.value,
                                      )
                                    }
                                    onBlur={() => handleBundleBlur(gIdx, bIdx, "price_single")}
                                    className="w-full bg-slate-50/50 pl-7 pr-3 py-2 rounded-lg text-sm outline-none border border-transparent focus:border-slate-200 focus:bg-white transition"
                                    placeholder="0.00"
                                    required
                                  />
                                </div>
                              </td>
                              <td className="px-2 py-3">
                                <div className="relative">
                                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-xs">
                                    £
                                  </span>
                                  <input
                                    type="number"
                                    step="0.01"
                                    value={bundle.price_double}
                                    onChange={(e) =>
                                      handleBundleChange(
                                        gIdx,
                                        bIdx,
                                        "price_double",
                                        e.target.value,
                                      )
                                    }
                                    onBlur={() => handleBundleBlur(gIdx, bIdx, "price_double")}
                                    className="w-full bg-slate-50/50 pl-7 pr-3 py-2 rounded-lg text-sm outline-none border border-transparent focus:border-slate-200 focus:bg-white transition"
                                    placeholder="0.00"
                                  />
                                </div>
                              </td>
                              <td className="px-2 py-3 text-right">
                                {group.bundles.length > 1 && (
                                  <button
                                    type="button"
                                    onClick={() => removeBundle(gIdx, bIdx)}
                                    className="text-slate-300 hover:text-red-500 p-2 transition"
                                  >
                                    <i className="fas fa-trash-alt text-xs"></i>
                                  </button>
                                )}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                    <button
                      type="button"
                      onClick={() => addBundle(gIdx)}
                      className="text-xs font-medium text-slate-600 hover:text-slate-900 flex items-center gap-1.5 ml-2 transition"
                    >
                      + Add bundle quantity
                    </button>
                  </div>
                </div>
              ))}

              <button
                type="button"
                onClick={addGroup}
                className="w-full py-5 rounded-2xl border-2 border-dashed border-slate-200 text-slate-400 hover:border-slate-400 hover:text-slate-600 transition flex items-center justify-center gap-2 text-sm font-medium bg-slate-50/50"
              >
                + Add Another Group
              </button>
            </div>

            {/* ADD-ONS */}
            <div className="p-6 rounded-2xl bg-slate-50/50 border border-slate-200">
              <div className="flex items-center justify-between mb-5">
                <h4 className="text-xs font-semibold text-slate-500 uppercase tracking-widest">
                  Available Add-ons
                </h4>
                <div className="flex items-center gap-3">
                  <select
                    disabled={masterExtras.length === 0}
                    onChange={(e) => {
                      const val = e.target.value;
                      if (!val) return;
                      const [id, name, price] = val.split("|");
                      handleFormChange("extras", [
                        ...form.extras,
                        { id, name, price: Number(price).toFixed(2) },
                      ]);
                      e.target.value = ""; // Reset dropdown
                    }}
                    className="text-xs border rounded-lg px-2 py-1 bg-white outline-none focus:border-slate-900 disabled:bg-slate-50 disabled:text-slate-400"
                  >
                    <option value="">
                      {masterExtras.length === 0 ? "No existing extras found" : "Select Existing..."}
                    </option>
                    {masterExtras.map((ex, idx) => (
                      <option key={idx} value={`${ex.id}|${ex.name}|${ex.price}`}>
                        {ex.name} (£{Number(ex.price).toFixed(2)})
                      </option>
                    ))}
                  </select>
                  <button
                    type="button"
                    onClick={addExtraRow}
                    className="text-xs font-medium text-slate-900 hover:underline"
                  >
                    + Add New Row
                  </button>
                </div>
              </div>
              <div className="grid gap-3 sm:grid-cols-2">
                {form.extras.map((ex, eIdx) => {
                  const isLinked = !!ex.id;
                  const isLocked = isLinked && !isEdit;
                  return (
                    <div
                      key={eIdx}
                      className={`flex gap-3 items-center p-3 rounded-xl border shadow-sm transition-all ${isLocked ? "bg-slate-100 border-slate-200 opacity-80" : "bg-white border-slate-200 hover:border-slate-300"}`}
                    >
                      <input
                        type="text"
                        value={ex.name}
                        disabled={isLocked}
                        onChange={(e) =>
                          handleExtraChange(eIdx, "name", e.target.value)
                        }
                        className={`flex-1 bg-transparent text-sm font-medium text-slate-700 outline-none placeholder:text-slate-300 ${isLocked ? "cursor-not-allowed" : ""}`}
                        placeholder="e.g. Gloss Finish"
                      />
                      <div className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg border ${isLocked ? "bg-slate-200 border-slate-300" : "bg-slate-50 border-slate-100"}`}>
                        <span className="text-xs font-medium text-slate-400">
                          £
                        </span>
                        <input
                          type="number"
                          step="0.01"
                          value={ex.price}
                          disabled={isLocked}
                          onChange={(e) =>
                            handleExtraChange(eIdx, "price", e.target.value)
                          }
                          onBlur={() => handleExtraBlur(eIdx)}
                          className={`w-16 bg-transparent text-sm font-medium text-slate-900 outline-none ${isLocked ? "cursor-not-allowed" : ""}`}
                        />
                      </div>
                      {isLinked && isEdit && (
                        <div className="group relative">
                          <i className="fas fa-link text-blue-500 text-[10px]" title="Shared Extra"></i>
                        </div>
                      )}
                      <button
                        type="button"
                        onClick={() => removeExtraRow(eIdx)}
                        className="text-slate-300 hover:text-red-500 transition px-1"
                      >
                        <i className="fas fa-times text-xs"></i>
                      </button>
                    </div>
                  );
                })}
                {form.extras.length === 0 && (
                  <p className="text-sm text-slate-400 italic">
                    No add-ons configured.
                  </p>
                )}
              </div>
            </div>
          </div>

          <div className="flex flex-col gap-3 border-t border-slate-200 px-6 py-5 sm:flex-row sm:items-center sm:justify-end bg-slate-50/50">
            <button
              type="button"
              onClick={onClose}
              disabled={saving}
              className="rounded-xl border border-slate-200 px-6 py-2.5 text-sm font-semibold text-slate-600 transition hover:bg-white hover:shadow-sm disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving || localLoading}
              className="rounded-xl bg-slate-900 px-10 py-2.5 text-sm font-semibold text-white transition hover:bg-slate-800 disabled:cursor-not-allowed disabled:opacity-60 shadow-lg"
            >
              {localLoading ? (
                <span className="flex items-center gap-2">
                  <div className="h-3 w-3 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                  Syncing Add-ons...
                </span>
              ) : saving ? (
                <span className="flex items-center gap-2">
                  <div className="h-3 w-3 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                  Saving Item...
                </span>
              ) : (
                isEdit ? "Update Print Item" : "Save Print Item"
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
